// Turbo Service bucket client — `/api/service/*` via manufacturingFetch.

import { manufacturingFetch } from "@/lib/manufacturingClient";
import type {
  ServiceEvent,
  ServiceOffering,
  EffectiveServiceOffering,
  ServiceRequest,
  ServiceRig,
  ServiceRoute,
  ServiceRouteDetail,
  ServiceRouteEvent,
  ServiceTech,
  ServiceWorkQueue,
} from "./types";

type OrgEnvelope = { ok: true; organization_id: string };

export function listServiceWorkQueues(opts?: { active_only?: boolean }) {
  const q = new URLSearchParams();
  if (opts?.active_only) q.set("active_only", "1");
  const qs = q.toString();
  const path = qs ? `/api/service/work-queues?${qs}` : "/api/service/work-queues";
  return manufacturingFetch<OrgEnvelope & { queues: ServiceWorkQueue[] }>(path, { method: "GET" });
}

export function upsertServiceWorkQueue(payload: {
  id?: string;
  label: string;
  slug?: string;
  default_rig_id?: string | null;
  default_duration_min?: number | null;
  default_approver_role?: string;
  auto_approve_manufacturing?: boolean;
  photos_required?: boolean;
  is_active?: boolean;
  sort_order?: number;
}) {
  return manufacturingFetch<OrgEnvelope & { queue: ServiceWorkQueue }>(
    "/api/service/work-queues/upsert",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function listServiceRequests(filters?: {
  status?: string;
  queue_id?: string;
  origin?: string;
}) {
  const q = new URLSearchParams();
  if (filters?.status) q.set("status", filters.status);
  if (filters?.queue_id) q.set("queue_id", filters.queue_id);
  if (filters?.origin) q.set("origin", filters.origin);
  const qs = q.toString();
  const path = qs ? `/api/service/requests?${qs}` : "/api/service/requests";
  return manufacturingFetch<OrgEnvelope & { requests: ServiceRequest[] }>(path, { method: "GET" });
}

export { createServiceRequest } from "./serviceClientWrites";

export function approveServiceRequest(payload: ServiceRequestReviewPayload) {
  return reviewServiceRequest({ ...payload, action: "approve" });
}

export function declineServiceRequest(payload: ServiceRequestReviewPayload) {
  return reviewServiceRequest({ ...payload, action: "decline" });
}

export function sendBackServiceRequest(payload: ServiceRequestReviewPayload & { review_notes: string }) {
  return reviewServiceRequest({ ...payload, action: "needs_info" });
}

export type ServiceRequestReviewPayload = {
  request_id: string;
  review_notes?: string;
  description?: string;
  location_label?: string;
  location_address?: string;
  queue_id?: string | null;
};

function reviewServiceRequest(payload: ServiceRequestReviewPayload & {
  action: "approve" | "decline" | "needs_info";
}) {
  return manufacturingFetch<OrgEnvelope & { request: ServiceRequest; event_id?: string }>(
    "/api/service/requests/review",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function listServiceEvents(filters?: {
  status?: string;
  queue_id?: string;
  scheduled_date_gte?: string;
  scheduled_date_lte?: string;
  tech_id?: string;
  assigned_rig_id?: string;
  rig_id?: string;
}) {
  const q = new URLSearchParams();
  if (filters?.status) q.set("status", filters.status);
  if (filters?.queue_id) q.set("queue_id", filters.queue_id);
  if (filters?.scheduled_date_gte) q.set("scheduled_date_gte", filters.scheduled_date_gte);
  if (filters?.scheduled_date_lte) q.set("scheduled_date_lte", filters.scheduled_date_lte);
  if (filters?.tech_id) q.set("tech_id", filters.tech_id);
  if (filters?.assigned_rig_id) q.set("assigned_rig_id", filters.assigned_rig_id);
  if (filters?.rig_id) q.set("rig_id", filters.rig_id);
  const qs = q.toString();
  const path = qs ? `/api/service/events?${qs}` : "/api/service/events";
  return manufacturingFetch<OrgEnvelope & { events: ServiceEvent[] }>(path, { method: "GET" });
}

export {
  updateServiceEvent,
  completeServiceEvent,
  assignServiceEventDispatch,
} from "./serviceClientWrites";

export function listServiceRoutes(filters?: {
  route_date?: string;
  route_date_gte?: string;
  route_date_lte?: string;
  tech_id?: string;
  include_events?: boolean;
}) {
  const q = new URLSearchParams();
  if (filters?.route_date) q.set("route_date", filters.route_date);
  if (filters?.route_date_gte) q.set("route_date_gte", filters.route_date_gte);
  if (filters?.route_date_lte) q.set("route_date_lte", filters.route_date_lte);
  if (filters?.tech_id) q.set("tech_id", filters.tech_id);
  if (filters?.include_events) q.set("include_events", "1");
  const qs = q.toString();
  const path = qs ? `/api/service/routes?${qs}` : "/api/service/routes";
  return manufacturingFetch<
    OrgEnvelope & { routes: ServiceRoute[] | ServiceRouteDetail[] }
  >(path, { method: "GET" });
}

export function createServiceRoute(payload: {
  route_date: string;
  tech_id?: string | null;
  rig_id?: string | null;
  label?: string;
}) {
  return manufacturingFetch<OrgEnvelope & { route: ServiceRoute }>(
    "/api/service/routes/create",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function assignServiceRouteEvent(payload: {
  route_id: string;
  event_id: string;
  sequence?: number;
}) {
  return manufacturingFetch<
    OrgEnvelope & {
      route_id: string;
      event_id: string;
      sequence: number;
      warnings?: string[];
    }
  >("/api/service/routes/assign-event", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function reorderServiceRouteEvents(payload: {
  route_id: string;
  ordered_event_ids: string[];
}) {
  return manufacturingFetch<OrgEnvelope & { route_events: ServiceRouteEvent[] }>(
    "/api/service/routes/reorder-events",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}
export function getServiceEventsCalendar(bounds: { scheduled_from: string; scheduled_to: string }) {
  const q = new URLSearchParams({
    scheduled_from: bounds.scheduled_from,
    scheduled_to: bounds.scheduled_to,
  });
  return manufacturingFetch<
    OrgEnvelope & {
      scheduled_from: string;
      scheduled_to: string;
      days: Record<string, ServiceEvent[]>;
    }
  >(`/api/service/calendar?${q.toString()}`, { method: "GET" });
}

export function listServiceTechs(opts?: { active_only?: boolean }) {
  const q = new URLSearchParams();
  if (opts?.active_only) q.set("active_only", "1");
  const qs = q.toString();
  const path = qs ? `/api/service/techs?${qs}` : "/api/service/techs";
  return manufacturingFetch<OrgEnvelope & { techs: ServiceTech[] }>(path, { method: "GET" });
}

export function upsertServiceTech(payload: {
  id?: string;
  display_name: string;
  phone?: string;
  skill_tags?: string[];
  is_active?: boolean;
  link_offering_id?: string | null;
}) {
  return manufacturingFetch<OrgEnvelope & { tech: ServiceTech }>("/api/service/techs/upsert", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function listServiceRigs(opts?: { active_only?: boolean }) {
  const q = new URLSearchParams();
  if (opts?.active_only) q.set("active_only", "1");
  const qs = q.toString();
  const path = qs ? `/api/service/rigs?${qs}` : "/api/service/rigs";
  return manufacturingFetch<OrgEnvelope & { rigs: ServiceRig[] }>(path, { method: "GET" });
}

export function upsertServiceRig(payload: {
  id?: string;
  designation: string;
  rig_kind?: string;
  capacity_value?: number | null;
  capacity_unit?: string | null;
  standard_tech_id?: string | null;
  is_active?: boolean;
  link_offering_id?: string | null;
}) {
  return manufacturingFetch<OrgEnvelope & { rig: ServiceRig }>("/api/service/rigs/upsert", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function listServiceOfferings(opts?: {
  include_inactive?: boolean;
  quotable_only?: boolean;
  effective?: boolean;
  /** @deprecated use include_inactive */
  active_only?: boolean;
}) {
  const q = new URLSearchParams();
  if (opts?.include_inactive || opts?.active_only === false) q.set("include_inactive", "1");
  if (opts?.quotable_only) q.set("quotable_only", "1");
  if (opts?.effective) q.set("effective", "1");
  const qs = q.toString();
  const path = qs ? `/api/service/offerings?${qs}` : "/api/service/offerings";
  return manufacturingFetch<OrgEnvelope & { offerings: ServiceOffering[] }>(path, { method: "GET" });
}

export function listEffectiveServiceOfferings(opts?: {
  include_inactive?: boolean;
  quotable_only?: boolean;
}) {
  const q = new URLSearchParams();
  q.set("effective", "1");
  if (opts?.include_inactive) q.set("include_inactive", "1");
  if (opts?.quotable_only) q.set("quotable_only", "1");
  const qs = q.toString();
  return manufacturingFetch<OrgEnvelope & { offerings: EffectiveServiceOffering[] }>(
    `/api/service/offerings?${qs}`,
    { method: "GET" }
  );
}

export function upsertServiceOffering(payload: {
  id?: string;
  label: string;
  slug: string;
  description?: string;
  default_queue_id?: string | null;
  default_rig_id?: string | null;
  default_duration_min?: number | null;
  default_mfg_recipe_id?: string | null;
  base_price_cents?: number | null;
  is_advertised?: boolean;
  quotable_from_sales?: boolean;
  is_active?: boolean;
}) {
  return manufacturingFetch<OrgEnvelope & { offering: ServiceOffering; product_types_synced?: boolean }>(
    "/api/service/offerings/upsert",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export {
  getServiceDefinitionsContext,
  patchServiceDefinition,
} from "./definitionsClient";

export {
  dismissServiceResourceNote,
  listServiceResourceNotes,
  upsertServiceResourceNote,
} from "./resourceNotesClient";

export {
  getServiceTechManifest,
  listServiceCheckIns,
  listServiceMapSuggestions,
  recordServiceCheckIn,
  stampServiceTechCheckIn,
} from "./fieldOpsClient";

export {
  getServiceCostPreview,
  getServiceCostSnapshot,
  listServiceJobActualMaterials,
  listServiceLaborRates,
  listServiceTaggedRecipes,
  upsertServiceJobActualMaterial,
  upsertServiceLaborRate,
} from "./costingClient";

export {
  listServiceEventLineItems,
  upsertServiceEventLineItem,
} from "./eventLineItemsClient";
